import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight } from '@phosphor-icons/react';
import {
  getListCompetitorsQueryKey,
  useListCompetitors,
  useReplaceCompetitors,
} from '@reelplicate/api-client/competitors';
import { OnboardingStep } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { Button, Field, FilterChip, Inline, Stack } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { AccountsEditor } from '@/features/competitors/accounts-editor';
import {
  accountsUsed,
  blankCompetitor,
  CompetitorsFormSchema,
  type CompetitorsFormValues,
  toCompetitorInputs,
  toCompetitorValues,
} from '@/features/competitors/competitor-form';

import { SiteInput } from './favicon';
import { OnboardingActions, OnboardingCard, OnboardingHeader } from './onboarding-card';
import { useOnboardingProgress } from './progress';
import { ONBOARDING_STEPS, stepPath } from './steps';

const ROWS = 3;
const empty = (): CompetitorsFormValues => ({
  competitors: Array.from({ length: ROWS }, blankCompetitor),
});

const SUGGESTIONS = [
  { name: 'Califia Farms', site: 'califiafarms.com' },
  { name: 'Alpro', site: 'alpro.com' },
  { name: 'Oatside', site: 'oatside.com' },
];

/**
 * Figma "Onboarding — 3 · Competitors" (126:817): three website fields
 * with a favicon slot - each with the TikTok and Instagram accounts it runs
 * (as many as it has, within the plan's allowance) that we track daily -
 * then suggested competitors as chips that fill the next empty field. Saved
 * to the workspace's competitor list; coming Back shows what was saved.
 */
export function CompetitorsStepPage() {
  const navigate = useNavigate();
  const suggestionsId = useId();
  const form = useForm<CompetitorsFormValues>({
    resolver: zodResolver(CompetitorsFormSchema),
    defaultValues: empty(),
  });
  const competitors = useWatch({ control: form.control, name: 'competitors' });
  const { advance } = useOnboardingProgress();
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id;
  const { data: saved } = useListCompetitors(workspaceId ?? '', {
    query: { enabled: Boolean(workspaceId) },
  });
  const replace = useReplaceCompetitors({ mutation: { meta: { silent: true } } });
  // Starter's allowance until a plan is chosen (step 5).
  const limit = saved?.limit ?? 3;

  // Back from a later step: show what was saved (in at least three rows).
  useEffect(() => {
    if (!saved?.items.length || form.formState.isDirty) return;
    const rows = toCompetitorValues(saved.items).map((row) => ({
      ...row,
      accounts: row.accounts.length ? row.accounts : blankCompetitor().accounts,
    }));
    while (rows.length < ROWS) rows.push(blankCompetitor());
    form.reset({ competitors: rows });
  }, [saved, form]);

  const onSubmit = form.handleSubmit(async ({ competitors: rows }) => {
    if (!workspaceId) return;
    try {
      await replace.mutateAsync({
        id: workspaceId,
        data: { competitors: toCompetitorInputs(rows) },
      });
    } catch (error) {
      form.setError('competitors.0.website', { message: saveError(error) });
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: getListCompetitorsQueryKey(workspaceId),
    });
    if (await advance(OnboardingStep.PLATFORMS)) await navigate(stepPath(4));
  });

  const normalise = (site: string) =>
    site
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/.*$/, '');
  const used = new Set(competitors.map((c) => normalise(c.website)));
  const suggestions = SUGGESTIONS.filter((s) => !used.has(s.site));
  const emptyIndex = competitors.findIndex((c) => c.website.trim() === '');

  return (
    <OnboardingCard loose onSubmit={onSubmit}>
      <OnboardingHeader
        eyebrow={`Step 3 of ${ONBOARDING_STEPS.length}`}
        title="Who are you up against?"
        subtitle="Add your competitors, with the TikTok and Instagram accounts they run if you know them - some run several. We check those daily for the posts beating their usual views."
      />

      <Stack gap={5}>
        {competitors.map((_, i) => (
          <Stack key={i} gap={2}>
            <Controller
              name={`competitors.${i}.website`}
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label={`Competitor ${i + 1}`} error={fieldState.error?.message}>
                  <SiteInput
                    {...field}
                    type="url"
                    inputMode="url"
                    placeholder="https://"
                  />
                </Field>
              )}
            />
            <AccountsEditor
              control={form.control}
              setValue={form.setValue}
              index={i}
              name={`Competitor ${i + 1}`}
              canAdd={accountsUsed(competitors) < limit}
              size="sm"
            />
          </Stack>
        ))}
        {suggestions.length > 0 && emptyIndex !== -1 && (
          <Stack gap={2}>
            <p id={suggestionsId} className="text-label-md">
              Suggested for oat milk brands
            </p>
            <Inline as="div" role="group" aria-labelledby={suggestionsId} gap={2} wrap>
              {suggestions.map((s) => (
                <FilterChip
                  key={s.site}
                  aria-label={`Add ${s.name}`}
                  onSelectedChange={() =>
                    form.setValue(`competitors.${emptyIndex}.website`, s.site, {
                      shouldValidate: form.formState.isSubmitted,
                    })
                  }
                >
                  + {s.name}
                </FilterChip>
              ))}
            </Inline>
          </Stack>
        )}
      </Stack>

      <OnboardingActions back={stepPath(2)}>
        <Button
          type="submit"
          size="lg"
          trailingIcon={ArrowRight}
          loading={form.formState.isSubmitting}
        >
          Continue
        </Button>
      </OnboardingActions>
    </OnboardingCard>
  );
}

/** The API's message for a rejected list (e.g. "That’s your own website…"), else a retry hint. */
function saveError(error: unknown) {
  const message = (
    error as { response?: { status?: number; data?: { message?: unknown } } }
  ).response;
  if (
    (message?.status === 400 || message?.status === 403) &&
    typeof message.data?.message === 'string'
  ) {
    return message.data.message;
  }
  return 'Couldn’t save your competitors. Try again.';
}
